import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getMe } from "../lib/api";
import { setSession } from "../lib/auth";
import { orgFromHost } from "../lib/format";
import { Alert, Loading } from "../components/States";
import { Logo } from "../components/Logo";

/**
 * Landing route for the SSO callback. The ac-edge `/api/auth/sso/callback`
 * mints a session and 302s the browser to `/sso-complete#token=<token>`. We
 * read the token from the URL fragment (never sent to a server), validate it
 * with GET /api/auth/me to load the signed-in user, store it in the SAME
 * session store the password login uses, then redirect into the app.
 */
export function SsoCompletePage() {
  const navigate = useNavigate();
  const org = orgFromHost();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 strict-mode double-invoke
    ran.current = true;

    const fragment = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(fragment);
    const token = params.get("token");

    // The edge signals a site-access denial via #error=no_access (identity was
    // proven, but the account isn't granted tenant access).
    if (params.get("error") === "no_access") {
      setError("Your account doesn't have access to this workspace. Contact an administrator.");
      return;
    }
    if (!token) {
      setError("No session token was returned by the identity provider.");
      return;
    }

    const ctrl = new AbortController();
    // Validate the token and load the user before persisting the session.
    getMe(token, ctrl.signal)
      .then((me) => {
        setSession({ token, user: me.user });
        // Strip the token from the address bar, then enter the app.
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/tickets", { replace: true });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(
          err instanceof Error
            ? `Sign-in could not be completed: ${err.message}`
            : "Sign-in could not be completed.",
        );
      });
    return () => ctrl.abort();
  }, [navigate]);

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="logo">
          <Logo size={26} /> {org}
        </div>
        {error ? (
          <>
            <Alert kind="error">{error}</Alert>
            <Link className="btn primary" to="/login" style={{ width: "100%", justifyContent: "center" }}>
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <p className="lead">Completing sign-in…</p>
            <Loading label="Finishing single sign-on…" />
          </>
        )}
      </div>
    </div>
  );
}
